export type UtmParams = Partial<Record<'utm_source' | 'utm_content', string>>;

const UTM_KEYS = ['utm_source', 'utm_content'] as const;
const MAX_UTM_VALUE_LENGTH = 128;
let currentPageUtm: UtmParams = {};

const normalizeUtmValue = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return trimmed.slice(0, MAX_UTM_VALUE_LENGTH);
};

export const sanitizeUtmParams = (params: UtmParams = {}): UtmParams => {
  const sanitized: UtmParams = {};

  UTM_KEYS.forEach(key => {
    const value = normalizeUtmValue(params[key]);
    if (value) sanitized[key] = value;
  });

  return sanitized;
};

export const hasUtmParams = (params: UtmParams = {}) =>
  Boolean(params.utm_source || params.utm_content);

export const readUtmParamsFromSearch = (search: string): UtmParams => {
  const searchParams = new URLSearchParams(search);
  const parsed: UtmParams = {};

  UTM_KEYS.forEach(key => {
    const value = normalizeUtmValue(searchParams.get(key));
    if (value) parsed[key] = value;
  });

  return parsed;
};

export const mergeUtmParams = (primary: UtmParams = {}, fallback: UtmParams = {}): UtmParams =>
  sanitizeUtmParams({ ...fallback, ...primary });

export const setCurrentPageUtm = (params: UtmParams = {}) => {
  currentPageUtm = sanitizeUtmParams(params);
};

export const getCurrentPageUtm = (): UtmParams => currentPageUtm;

interface ExtractUtmResult {
  utmParams: UtmParams;
  cleanedSearch: string;
  hasUtmParamsInSearch: boolean;
}

export const extractUtmFromSearch = (search: string): ExtractUtmResult => {
  const searchParams = new URLSearchParams(search);
  const collected: UtmParams = {};
  let hasUtmParamsInSearch = false;

  const keys = Array.from(new Set(Array.from(searchParams.keys())));
  keys.forEach(key => {
    if (!key.startsWith('utm_')) return;

    hasUtmParamsInSearch = true;
    const value = searchParams.get(key);
    if (key === 'utm_source' || key === 'utm_content') {
      const normalized = normalizeUtmValue(value);
      if (normalized) collected[key] = normalized;
    }

    searchParams.delete(key);
  });

  return {
    utmParams: sanitizeUtmParams(collected),
    cleanedSearch: searchParams.toString(),
    hasUtmParamsInSearch,
  };
};
